import React, { useContext } from "react";
import { Button as AntdButton, Col, Row, ButtonProps as AntdButtonProps } from "antd";
import { IoIosAdd } from "react-icons/io";
import { useModal } from "react-modal-hook";
import { useTranslation } from "react-i18next";
import ProductContext from "../../../../context/product/context";
import FormHookModal from "../../modals/form-hook-modal";
import FormItem from "../../form-item";
import { Controller, useForm, FormProvider } from "react-hook-form";
import Input from "../../../form-components/input";
import { IQuantity } from "../../../../models/product/response";
import { IAddQuantity } from "../../../../models/product/request";

export interface ButtonProps extends AntdButtonProps {
  passedId: number;
  quantities: IQuantity[];
}

interface IFormValues {
  stores: {
    storeId: number;
    storeName: string;
    addedQuantity: number;
  }[];
}


const AddQuantityButton: React.FC<ButtonProps> = ({ passedId, quantities, ...props }) => {
  const { t } = useTranslation();
  const { actions, loading } = useContext(ProductContext);

  const [showFormModal, hideFormModal] = useModal(() => {
    const methods = useForm<IFormValues>({
      defaultValues: {
        stores: quantities.map((q) => ({
          storeId: q.store_id,
          storeName: q.store_name,
          addedQuantity: 0,
        })),
      },
    });
    const { control, handleSubmit } = methods;

    const onSubmit = async (data: IFormValues) => {
      const payload: IAddQuantity = {
        storeQuantities: data.stores.map((s) => ({
          store_id: s.storeId,
          added_quantity: Number(s.addedQuantity),
        })),
      };

      await actions.addQuantity(passedId, payload);
      actions.getData();
      hideFormModal();
    };

    return (
      <FormHookModal
        id="add-quantity-modal"
        confirmButtonText={t("add")}
        loading={loading.includes("addQuantity")}
        title={t("add_quantity")}
        hideModal={hideFormModal}
        width="50vw"
        style={{ marginTop: "1.6rem", maxHeight: "70vh", overflowY: "auto" }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <FormProvider {...methods}>
          <form>
            {quantities.map((q, index) => (
              <Row key={q.store_id} gutter={[16, 8]} style={{ marginBottom: 8 }}>
                <Col span={12}>
                  <strong>{q.store_name}</strong>
                </Col>
                <Col span={12}>
                  <FormItem label={t("added_quantity")}>
                    <Controller
                      name={`stores.${index}.addedQuantity`}
                      control={control}
                      rules={{
                        required: { value: true, message: t("field_is_required_message") },
                        validate: (value) => value > 0 || t("must_be_positive"),
                      }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="number"
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      )}
                    />
                  </FormItem>
                </Col>
              </Row>
            ))}
          </form>
        </FormProvider>
      </FormHookModal>
    );
  }, [passedId, quantities, loading]);

  return (
    <AntdButton type="primary" icon={<IoIosAdd />} onClick={showFormModal} {...props} />
  );
};

export default AddQuantityButton;
